import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Download, 
  Apple, 
  Monitor, 
  Shield,
  CheckCircle,
  ExternalLink
} from "lucide-react";
import {
  fetchLatestInstallerRelease,
} from "@/lib/installer-downloads";

interface DownloadEntry { url: string; filename: string; size: number }

interface DownloadItem {
  platform: string;
  icon: React.ReactNode;
  description: string;
  filename: string;
  url: string;
  size: string;
  requirements: string;
  verified: boolean;
}

function formatBytes(bytes: number): string {
  return `~${Math.round(bytes / 1024 / 1024)} MB`;
}

const DownloadPage = () => {
  const [latest, setLatest] = useState<{
    date: string;
    windows: DownloadEntry;
    armDmg: DownloadEntry;
    x64Dmg: DownloadEntry;
  } | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setLatest(null);
    setLoadError(false);
    fetchLatestInstallerRelease()
      .then((data) => {
        const assets = data.assets || [];
        const version = data.tag_name?.replace(/^v/, '');
        const find = (name: string): DownloadEntry | undefined => {
          const asset = assets.find((item) => item.name === name);
          return asset?.download_url && asset.size > 0
            ? { url: asset.download_url, filename: asset.name, size: asset.size }
            : undefined;
        };
        const windows = find(`Work.Time.Setup.${version}.exe`);
        const armDmg = find(`Work.Time-${version}-arm64.dmg`);
        const x64Dmg = find(`Work.Time-${version}.dmg`);
        if (!version || !windows || !armDmg || !x64Dmg) {
          throw new Error('Latest release has incomplete installers');
        }
        if (active) setLatest({
          date: data.published_at ? new Date(data.published_at).toLocaleDateString() : 'Latest release',
          windows,
          armDmg,
          x64Dmg,
        });
      })
      .catch((err) => {
        console.warn('Could not load latest installers:', err);
        if (active) setLoadError(true);
      });
    return () => { active = false; };
  }, [retry]);

  const downloads: DownloadItem[] = latest ? [
    {
      platform: "macOS (Apple Silicon)",
      icon: <Apple className="h-6 w-6" />,
      description: "For M1, M2, M3, M4 Macs",
      filename: latest.armDmg.filename,
      url: latest.armDmg.url,
      size: formatBytes(latest.armDmg.size),
      requirements: "macOS 11.0+",
      verified: true,
    },
    {
      platform: "macOS (Intel)",
      icon: <Apple className="h-6 w-6" />,
      description: "For Intel-based Macs",
      filename: latest.x64Dmg.filename,
      url: latest.x64Dmg.url,
      size: formatBytes(latest.x64Dmg.size),
      requirements: "macOS 10.14+",
      verified: true,
    },
    {
      platform: "Windows",
      icon: <Monitor className="h-6 w-6" />,
      description: "For Windows 10/11",
      filename: latest.windows.filename,
      url: latest.windows.url,
      size: formatBytes(latest.windows.size),
      requirements: "Windows 10/11 (64-bit)",
      verified: true,
    },
  ] : [];

  const handleDownload = (url: string, filename: string) => {
    // Use anchor with download attribute for direct download without opening GitHub page
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Download Work Time
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            Professional employee time tracking desktop application
          </p>
          <div className="flex justify-center items-center gap-4 mb-8">
            <Badge variant="secondary" className="text-lg px-4 py-2">
              Latest installers
            </Badge>
            <Badge variant="outline" className="text-sm">
              {latest ? `Released ${latest.date}` : 'Checking latest release'}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
          {[
            { icon: <Shield className="h-5 w-5" />, text: "Code Signed & Verified" },
            { icon: <CheckCircle className="h-5 w-5" />, text: "Enterprise Security" },
            { icon: <Download className="h-5 w-5" />, text: "Auto Updates" },
            { icon: <Monitor className="h-5 w-5" />, text: "Cross Platform" }
          ].map((feature, index) => (
            <div key={index} className="flex items-center gap-2 text-sm text-gray-600 bg-white/50 rounded-lg p-3">
              {feature.icon}
              <span>{feature.text}</span>
            </div>
          ))}
        </div>

        {!latest && (
          <div role="status" className="mb-12 rounded-lg bg-white p-6 text-center text-gray-700">
            {loadError ? (
              <>
                <p>We couldn't load the latest installers. Please try again.</p>
                <Button className="mt-4" onClick={() => setRetry((value) => value + 1)}>Retry</Button>
              </>
            ) : 'Checking the latest installers…'}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {downloads.map((download, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  {download.icon}
                  <CardTitle className="text-xl">{download.platform}</CardTitle>
                  {download.verified && (
                    <Badge className="bg-green-100 text-green-800">
                      <Shield className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                </div>
                <CardDescription>{download.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-sm text-gray-600">
                    <div>File: {download.filename}</div>
                    <div>Size: {download.size}</div>
                    <div>Requires: {download.requirements}</div>
                  </div>
                  <Button 
                    onClick={() => handleDownload(download.url, download.filename)}
                    className="w-full"
                    size="lg"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download for {download.platform}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Installation Instructions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg mb-2">macOS</h3>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                <li>Download the appropriate DMG file for your Mac</li>
                <li>Open the downloaded DMG file</li>
                <li>Drag "Work Time.app" to your Applications folder</li>
                <li>Eject the DMG and launch the app from Applications</li>
                <li>If prompted about security, go to System Preferences &rarr; Security &amp; Privacy &rarr; "Open Anyway"</li>
              </ol>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Windows</h3>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                <li>Download the EXE installer</li>
                <li>Open the installer normally using your own Windows account</li>
                <li>If prompted, review and approve the Ebdaa publisher certificate and Windows certificate confirmation</li>
                <li>Launch from Start Menu or Desktop shortcut</li>
              </ol>
              <p className="mt-3 text-sm text-gray-600">
                If an older version is stuck on Update Required, download and run this setup once.
                It can establish publisher trust for your Windows account so future updates can install normally.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center text-gray-600 space-y-4">
          {latest && <div className="flex justify-center gap-6 text-sm">
            <a
              href={latest.armDmg.url}
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-blue-600"
            >
              <ExternalLink className="h-4 w-4" />
              Direct link: macOS (Apple Silicon)
            </a>
            <a
              href={latest.x64Dmg.url}
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-blue-600"
            >
              <ExternalLink className="h-4 w-4" />
              Direct link: macOS (Intel)
            </a>
            <a
              href={latest.windows.url}
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-blue-600"
            >
              <ExternalLink className="h-4 w-4" />
              Direct link: Windows
            </a>
          </div>}
          <p className="text-sm">
            All downloads are code-signed and verified for security
          </p>
          <p className="text-xs text-gray-500">
            &copy; {new Date().getFullYear()} Ebdaa Digital Technology. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
